import { useEffect, useRef, useState } from 'react';
import { getResearch, apiError } from '../../lib/research';

export const useLiveCrypto = ({ coinId, symbol, period, active, root }) => {
  const [state, setState] = useState({ data: null, error: '', loading: true, key: '' });
  const [bump, setBump] = useState(0), [visible, setVisible] = useState(!document.hidden), [inView, setInView] = useState(true);
  const delay = useRef(60);
  const key = `${coinId || symbol}:${period}`;
  useEffect(() => {
    const change = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', change);
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));
    if (root.current) observer.observe(root.current);
    return () => { document.removeEventListener('visibilitychange', change); observer.disconnect(); };
  }, [root]);
  useEffect(() => {
    if (!active || !visible || !inView) return;
    let timer, stopped = false;
    const abort = new AbortController();
    const tick = async () => {
      setState(old => ({ ...old, key, data: old.key === key ? old.data : null, loading: true, error: old.key === key ? old.error : '' }));
      let next = delay.current;
      try {
        const { data } = await getResearch('/crypto/chart', { params: { ...(coinId ? { coin_id: coinId } : { symbol }), period }, signal: abort.signal, timeout: 45000 });
        if (stopped) return;
        if (data.period !== period || coinId && data.coin_id !== coinId || ![30,60,120,300].includes(data.refresh_seconds)) throw new Error('Chart identity or refresh settings did not match this request.');
        delay.current = data.refresh_seconds; next = data.refresh_seconds;
        setState({ data, error: '', loading: false, key });
      } catch (e) {
        if (stopped) return;
        const retryAfter = Number(e.response?.headers?.['retry-after']);
        next = retryAfter > 0 ? Math.min(120, retryAfter) : delay.current;
        if ([401,403,404,422].includes(e.response?.status)) next = null;
        setState(old => ({ ...old, loading: false, error: apiError(e), key }));
      }
      if (!stopped && next != null) timer = setTimeout(tick, next * 1000);
    };
    tick();
    return () => { stopped = true; clearTimeout(timer); abort.abort(); };
  }, [coinId, symbol, period, key, active, visible, inView, bump]);
  return { ...state, data: state.key === key ? state.data : null, refresh: () => setBump(n => n+1), paused: !visible || !inView };
};